// DEMO PARITY (v2.14.1) — ADAPTADOR: proyecta el fixture DEMO (en memoria) al MISMO contrato que
// devuelven los read-models de v2.14.0 (GET /api/radar/residencial y /api/radar/institucional).
// Así los dos demos históricos se pintan con la UI vigente del Radar Operativo SIN duplicar
// componentes y SIN tocar el backend: ninguna de estas funciones hace red ni escribe nada.
// El aislamiento se conserva: sólo se lee `demoState` (su propio fixture), nunca datos reales.
const VENTANA_MIN = 30;
const PRIO_LABEL = { alta: "Alta", maxima: "Alta", media: "Media", baja: "Baja" };
const norm = (p) => (p === "maxima" ? "alta" : p);
const rank = (p) => (norm(p) === "alta" ? 0 : norm(p) === "media" ? 1 : 2);

const casoDto = (a) => ({
  alarma_id: a.id, tipo: a.tipo, es_tecnica: !!a.es_tecnica,
  prioridad: norm(a.prioridad), prioridad_label: PRIO_LABEL[norm(a.prioridad)] || norm(a.prioridad),
  estado: a.estado, en_atencion: a.estado !== "recibida",
  operador: a.estado !== "recibida" ? a.operador_nombre || null : null,
  desde: a.hora_recepcion_servidor,
});

// Mismo orden operacional del backend: ALTA > MEDIA > (caso o actividad reciente) > cobertura > resto.
function categoria(t) {
  const c = t.caso;
  if (c && norm(c.prioridad) === "alta") return 0;
  if (c && c.prioridad === "media") return 1;
  if (c || t.actividad_reciente) return 2;
  if (["perdida", "no_confirmable", "degradada"].includes(t.cobertura_peor)) return 3;
  return 4;
}
function ordenar(tarjetas) {
  return [...tarjetas].sort((x, y) => {
    const dc = categoria(x) - categoria(y);
    if (dc) return dc;
    const rx = x.caso?.desde || x.actividad_reciente_hora || x.hechos[0]?.hora || "";
    const ry = y.caso?.desde || y.actividad_reciente_hora || y.hechos[0]?.hora || "";
    if (rx !== ry) return rx < ry ? 1 : -1;
    return (x.nombre || "").toLowerCase().localeCompare((y.nombre || "").toLowerCase());
  });
}

// Los eventos del fixture identifican a la persona por NOMBRE (con tratamiento «Don/Doña»).
const hechosDe = (eventos, nombre) => eventos
  .filter((e) => e.persona_nombre && (e.persona_nombre.includes(nombre) || nombre.includes(e.persona_nombre)))
  .map((e) => ({ hora: e.hora_recepcion_servidor, tipo: e.tipo_evento }))
  .sort((a, b) => (a.hora < b.hora ? 1 : -1));

function completar(t, ahoraMs) {
  const reciente = t.hechos.find((h) => ahoraMs - Date.parse(h.hora) <= VENTANA_MIN * 60000);
  t.actividad_reciente = !!reciente;
  t.actividad_reciente_hora = reciente?.hora || null;
  t.categoria = categoria(t);
  return t;
}

const resumen = (tarjetas, clave) => ({
  [clave]: tarjetas.length,
  pendientes: tarjetas.filter((t) => t.caso && !t.caso.en_atencion).length,
  en_atencion: tarjetas.filter((t) => t.caso && t.caso.en_atencion).length,
  actividad_reciente: tarjetas.filter((t) => t.actividad_reciente).length,
  coberturas_revisar: tarjetas.filter((t) => ["perdida", "no_confirmable", "degradada"].includes(t.cobertura_peor)).length,
  atenciones_activas: tarjetas.reduce((n, t) => n + (t.casos_activos || 0), 0),
});

// Casos activos por persona, con el principal resuelto igual que en el resto del sistema.
function casosPorPersona(demo) {
  const out = {};
  demo.alarmas.filter((a) => a.estado !== "cerrada").forEach((a) => {
    const pid = demo._caseToPerson?.[a.id];
    if (pid) (out[pid] = out[pid] || []).push(a);
  });
  Object.values(out).forEach((l) => l.sort((x, y) => rank(x.prioridad) - rank(y.prioridad)
    || Date.parse(x.hora_recepcion_servidor) - Date.parse(y.hora_recepcion_servidor)));
  return out;
}

// ---------- DEMO CENTRAL RESIDENCIAL (30 personas) → contrato de /api/radar/residencial ----------
export function snapshotResidencial(demo) {
  const ahoraMs = Date.now();
  const casos = casosPorPersona(demo);
  const tarjetas = (demo._cartera || []).map((c) => {
    const l = casos[c.id] || [];
    return completar({
      id: c.id, nombre: c.nombre, edad: c.edad, sitio_id: "demo",
      sitio_nombre: c.direccion, ciudad: c.ciudad, habitacion: "", zona_nombre: "", cama_etiqueta: "",
      caso: l[0] ? casoDto(l[0]) : null, casos_activos: l.length,
      cobertura_peor: "protegida", cobertura: [],
      hechos: hechosDe(demo.eventos || [], c.nombre),
    }, ahoraMs);
  });
  const orden = ordenar(tarjetas);
  return {
    ahora: new Date(ahoraMs).toISOString(), ventana_actividad_min: VENTANA_MIN,
    resumen: resumen(orden, "personas"), personas: orden,
  };
}

// ---------- DEMO INSTITUCIONAL (20 camas) → contrato de /api/radar/institucional ----------
// El fixture describe habitaciones compartidas pero no etiquetas de cama: se derivan de forma
// determinista (A, B, …) por el orden del residente en su habitación, para conservar la jerarquía
// CENTRO -> HABITACIÓN -> CAMA -> RESIDENTE de la demostración de 20 camas.
export function snapshotInstitucional(demo) {
  const ahoraMs = Date.now();
  const casos = casosPorPersona(demo);
  const camaPorHab = {};
  const tarjetas = (demo._personas || []).map((p) => {
    const l = casos[p.id] || [];
    const n = (camaPorHab[p.room] = (camaPorHab[p.room] || 0) + 1);
    const degradada = !!p.coberturaMarca;
    return completar({
      id: p.id, nombre: p.nombre, edad: p.edad, sitio_id: "demo-inst",
      sitio_nombre: demo.sitioNombre, ciudad: demo._sitio?.ciudad || "",
      habitacion: p.room, zona_nombre: p.room,
      cama_etiqueta: String.fromCharCode(64 + n),
      caso: l[0] ? casoDto(l[0]) : null, casos_activos: l.length,
      cobertura_peor: degradada ? "degradada" : "protegida",
      cobertura: degradada ? [{ capacidad: "caida", estado: "degradada", label: "Cobertura degradada" }] : [],
      hechos: hechosDe(demo.eventos || [], p.nombre),
    }, ahoraMs);
  });

  const grupos = {};
  tarjetas.forEach((t) => { (grupos[t.habitacion || "Sin habitación"] = grupos[t.habitacion || "Sin habitación"] || []).push(t); });
  const habitaciones = Object.keys(grupos).sort().map((nombre) => ({
    nombre,
    residentes: grupos[nombre].sort((a, b) => (a.cama_etiqueta || "~").localeCompare(b.cama_etiqueta || "~")),
  }));

  const atenciones = ordenar(tarjetas.filter((t) => t.caso)).map((t) => ({
    alarma_id: t.caso.alarma_id, persona_id: t.id, persona: t.nombre,
    habitacion: t.habitacion, cama: t.cama_etiqueta,
    tipo: t.caso.tipo, es_tecnica: t.caso.es_tecnica,
    prioridad: t.caso.prioridad, prioridad_label: t.caso.prioridad_label,
    estado: t.caso.estado, en_atencion: t.caso.en_atencion,
    operador: t.caso.operador, desde: t.caso.desde, casos_activos: t.casos_activos,
    // En el demo institucional el caso lo atiende el PERSONAL DEL CENTRO, nunca Central Galarya.
    responsable: demo.actor?.org || null,
  }));

  const coberturas = tarjetas.flatMap((t) => t.cobertura.map((c) => ({
    persona_id: t.id, persona: t.nombre, habitacion: t.habitacion,
    capacidad: c.capacidad, estado: c.estado, label: c.label,
  })));

  // R02 · el DEMO comparte los componentes del Radar Institucional, así que proyecta TAMBIÉN el
  // bloque `radar-institucional-r02-1` (casos por ID, directorio de habitaciones/camas, contexto de
  // cobertura y metadatos de lectura). Los IDs de habitación/cama son del fixture en memoria.
  const zonaIdDe = (hab) => `demo-zona-${String(hab).replace(/\s+/g, "-").toLowerCase()}`;
  const casosR02 = ordenar(tarjetas.filter((t) => t.caso)).flatMap((t) => (casos[t.id] || []).map((a) => {
    const c = casoDto(a);
    return {
      ...c,
      persona_id: t.id, persona_nombre: t.nombre,
      ubicacion: { nivel: "cama", zona_id: zonaIdDe(t.habitacion), habitacion: t.habitacion,
        cama_id: `demo-cama-${t.id}`, cama_etiqueta: t.cama_etiqueta, motivo: null },
    };
  }));
  const r02Habitaciones = habitaciones.map((h) => ({
    zona_id: zonaIdDe(h.nombre), nombre: h.nombre, tipo: null,
    camas: h.residentes.map((t) => ({ cama_id: `demo-cama-${t.id}`, etiqueta: t.cama_etiqueta,
      persona_id: t.id, persona_nombre: t.nombre })),
  }));
  const r02Cobertura = tarjetas.flatMap((t) => t.cobertura.map((c) => ({
    persona_id: t.id, capacidad: c.capacidad, estado: c.estado, origen: "explicito",
    dispositivos_total: 1, dispositivos_caidos: c.estado === "perdida" ? 1 : 0,
  })));
  const r02 = {
    contrato: "radar-institucional-r02-1",
    casos: casosR02,
    resumen_casos: {
      total: casosR02.length,
      humanos: casosR02.filter((c) => !c.es_tecnica).length,
      tecnicos: casosR02.filter((c) => c.es_tecnica).length,
      pendientes: casosR02.filter((c) => !c.en_atencion).length,
      en_atencion: casosR02.filter((c) => c.en_atencion).length,
      otros_estados: 0,
    },
    habitaciones: r02Habitaciones,
    cobertura_contexto: r02Cobertura,
    lectura: { casos_total_consultados: casosR02.length, casos_devueltos: casosR02.length,
      completo: true, limite: 60, error_parcial: null, hay_mas: false, cursor_siguiente: null },
  };

  return {
    ahora: new Date(ahoraMs).toISOString(), ventana_actividad_min: VENTANA_MIN,
    sitio: { id: "demo-inst", nombre: demo.sitioNombre, ciudad: demo._sitio?.ciudad || "",
      direccion: demo._sitio?.direccion || "", modalidad: "institucional" },
    resumen: resumen(tarjetas, "residentes"),
    atenciones, habitaciones, coberturas, residentes: tarjetas, r02,
  };
}
